import { makeVar, useReactiveVar } from '@apollo/client'
import { useCallback, useRef } from 'react'
import invariant from 'tiny-invariant'

const optimisticUpdateCounterVar = makeVar<number>(0)

export const useIsCartInFlightRequests = (): boolean => !!useReactiveVar(optimisticUpdateCounterVar)

export const getIsCartInFlightRequests = (): boolean => Boolean(optimisticUpdateCounterVar())

interface Actions {
    increment: (itemId: string) => void
    lock: (itemId: string) => void
    reset: (itemId: string) => void
}

export const useCartOptimisticUpdateCounterActions = (): Actions => {
    const itemIdCountsRef = useRef<Record<string, number[]>>({})

    const increment = useCallback((itemId: string) => {
        optimisticUpdateCounterVar(optimisticUpdateCounterVar() + 1)

        if (!itemIdCountsRef.current[itemId]) itemIdCountsRef.current[itemId] = [0]

        const lastIndex = itemIdCountsRef.current[itemId].length - 1

        itemIdCountsRef.current[itemId][lastIndex] += 1
    }, [])

    const lock = useCallback((itemId: string) => {
        if (!itemIdCountsRef.current[itemId]) itemIdCountsRef.current[itemId] = [0]

        itemIdCountsRef.current[itemId].push(0)
    }, [])

    const reset = useCallback((itemId: string) => {
        const currentLockedCount = itemIdCountsRef.current[itemId].shift()

        invariant(
            currentLockedCount !== undefined,
            "'reset' can be called only if locked non-zero count is present"
        )

        optimisticUpdateCounterVar(optimisticUpdateCounterVar() - currentLockedCount)

        if (
            itemIdCountsRef.current[itemId].length === 1 &&
            itemIdCountsRef.current[itemId][0] === 0
        ) {
            delete itemIdCountsRef.current[itemId]
        }
    }, [])

    return { increment, lock, reset }
}
